import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Rating,
  Button,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  LinearProgress,
  useMediaQuery,
  Pagination,
  Paper,
  Stack,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { UserContext } from "../../context/userContext/userContext";
import { useStyle } from "../../context/styleContext/styleContext";
import SubscribeRedirectModal from "../../components/SubscribeRedirectModal/SubscribeRedirectModal";
import LoginRegisterModal from "./LoginRegisterModal";
import LeaveReviewModal from "./leaveReviewModal";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import Seo from "../../components/Seo";
import { Link, useParams } from "react-router-dom";
import ProductReviewSchemaMarkup from "./ProductReviewSchemaMarkup";

const REVIEWS_PER_PAGE = 3;

export default function ProductReviewPage() {
  const { user } = useContext(UserContext);
  const { style } = useStyle();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { itemName } = useParams();

  const [reviews, setReviews] = useState([]);
  const [productSKU, setProductSKU] = useState(null);
  const [productData, setProductData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("recent");
  const [currentPage, setCurrentPage] = useState(1);
  const [ratingsBreakdown, setRatingsBreakdown] = useState([0, 0, 0, 0, 0]);
  const [averageRating, setAverageRating] = useState(0);
  const [height, setHeight] = useState(window.innerHeight);

  const [isSubscribeRedirectModalOpen, setSubscribeRedirectModalOpen] = useState(false);
  const [isLoginRegisterModalOpen, setLoginRegisterModalOpen] = useState(false);
  const [isLeaveReviewModalOpen, setLeaveReviewModalOpen] = useState(false);

  const [storedReviewId, setStoredReviewId] = useState(null);
  const [storedVote, setStoredVote] = useState(null);

  const closeModal = (setModalFn) => () => setModalFn(false);

  useEffect(() => {
    getReviews();
  }, [itemName, filter, sort]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, sort]);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loginCallback = async () => {
    const token = localStorage.getItem("token");
    if (!token || !storedReviewId || !storedVote) {
      return;
    }

    try {
      await axios.post("/api/mark_review", {
        token,
        reviewId: storedReviewId,
        type: storedVote,
      });

      setLoginRegisterModalOpen(false);
      setStoredReviewId(null);
      setStoredVote(null);
      getReviews();
    } catch (error) {
      console.log("Error marking review vote after login:", error);
    }
  };

  const getReviews = async () => {
    try {
      const { data: productInfo } = await axios.post("/get_product_by_uri", {
        productURI: itemName,
      });

      if (!productInfo) return;

      setProductSKU(productInfo.SKU);
      setProductData(productInfo);

      const { data } = await axios.post("/get_all_product_reviews", {
        productSKU: productInfo.SKU,
        filter,
        sort,
      });

      const loadedReviews = data || [];
      setReviews(loadedReviews);

      const starCounts = [0, 0, 0, 0, 0];
      let total = 0;
      let sum = 0;

      loadedReviews.forEach((review) => {
        const index = 5 - Number(review.starRating);
        if (index >= 0 && index < 5) {
          starCounts[index] += 1;
        }
        total += 1;
        sum += Number(review.starRating || 0);
      });

      setAverageRating(total ? Number((sum / total).toFixed(1)) : 0);
      setRatingsBreakdown(starCounts);
    } catch (error) {
      console.log("Error loading reviews:", error);
    }
  };

  const handleVote = async (reviewId, type) => {
    const token = localStorage.getItem("token");

    if (!token || !user) {
      setStoredVote(type);
      setStoredReviewId(reviewId);
      setLoginRegisterModalOpen(true);
      return;
    }

    try {
      await axios.post("/api/mark_review", {
        token,
        reviewId,
        type,
      });

      getReviews();
    } catch (error) {
      console.log("Error marking review vote:", error);
    }
  };

  const formatDate = (dateStr) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateStr).toLocaleDateString(undefined, options);
  };

  const filteredReviews = useMemo(() => {
    if (filter === "all") return reviews;
    return reviews.filter((review) => Number(review.starRating) === Number(filter));
  }, [reviews, filter]);

  const totalPages = Math.ceil(filteredReviews.length / REVIEWS_PER_PAGE);

  const paginatedReviews = useMemo(() => {
    return filteredReviews.slice(
      (currentPage - 1) * REVIEWS_PER_PAGE,
      currentPage * REVIEWS_PER_PAGE
    );
  }, [filteredReviews, currentPage]);

  return (
    <>
      <Seo
        title={
          productData
            ? `${productData.name} Reviews | Parish Audio`
            : "Plugin Reviews | Parish Audio"
        }
        description={
          productData
            ? `Read customer reviews for ${productData.name} by Parish Audio. See ratings, feedback, and user impressions for this VST3 plugin.`
            : "Read customer reviews for Parish Audio plugins."
        }
        canonical={
          productData
            ? `https://parishaudio.com/reviews/${productData.productURI}`
            : "https://parishaudio.com/reviews"
        }
        image={
          productData?.images?.[0]?.URI
            ? `https://parishaudio.com${productData.images[0].URI}`
            : "https://parishaudio.com/assets/og-default.jpg"
        }
        imageAlt={
          productData?.images?.[0]?.altText || productData?.name || "Parish Audio reviews"
        }
        ogType="website"
      />

      {productData && (
        <ProductReviewSchemaMarkup
          productData={productData}
          reviews={reviews}
          averageRating={averageRating}
        />
      )}

      {!productSKU ? (
        <Box
          sx={{
            minHeight: height - 170,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <LoadingSpinner />
        </Box>
      ) : (
        <Box
          sx={{
            maxWidth: 1000,
            margin: "auto",
            padding: { xs: 1.5, md: 2 },
            minHeight: height - 170,
          }}
        >
          <Paper
            sx={{
              p: { xs: 2, md: 3 },
              backgroundColor: style.color1,
              color: style.color4,
              borderRadius: 2,
              boxShadow: 10,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "280px minmax(0, 1fr)" },
                gap: 3,
                alignItems: "start",
              }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: "rgba(0,0,0,0.18)",
                  boxShadow: 8,
                }}
              >
                <Link
                  to={`/products/${productData.productURI}`}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <Box
                    component="img"
                    src={productData.images?.[0]?.URI}
                    alt={productData.images?.[0]?.altText}
                    sx={{
                      maxWidth: "90%",
                      maxHeight: 230,
                      objectFit: "contain",
                      display: "block",
                      mx: "auto",
                    }}
                  />
                  <Typography
                    variant="h5"
                    sx={{ mt: 1.5, fontSize: "1.2rem", color: style.color3, fontWeight: 700 }}
                  >
                    {productData.name}
                  </Typography>
                </Link>

                <Button
                  component={Link}
                  to={`/products/${productData.productURI}`}
                  variant="outlined"
                  sx={{
                    mt: 2,
                    color: style.color4,
                    borderColor: style.color2,
                    "&:hover": {
                      borderColor: style.color3,
                      backgroundColor: "rgba(255,255,255,0.05)",
                    },
                  }}
                >
                  View Product
                </Button>
              </Box>

              <Box>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2rem", md: "2.4rem" },
                    fontWeight: 800,
                    mb: 1,
                    lineHeight: 1.1,
                  }}
                >
                  {productData.name} Reviews
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: style.color3,
                    mb: 2,
                    maxWidth: 700,
                  }}
                >
                  Read customer feedback, ratings, and impressions for {productData.name}.
                </Typography>

                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={3}
                  sx={{ mb: 2.5 }}
                >
                  <Box sx={{ minWidth: { md: 220 } }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                      <Rating value={parseFloat(averageRating)} precision={0.1} readOnly />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {averageRating} / 5
                      </Typography>
                    </Stack>

                    <Typography variant="body2" sx={{ color: style.color3 }}>
                      Based on {reviews.length} review{reviews.length === 1 ? "" : "s"}
                    </Typography>
                  </Box>

                  <Box sx={{ flexGrow: 1 }}>
                    {[5, 4, 3, 2, 1].map((star) => {
                      const count = ratingsBreakdown[5 - star] || 0;
                      const percent = reviews.length ? (count / reviews.length) * 100 : 0;

                      return (
                        <Box
                          key={star}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mb: 1,
                          }}
                        >
                          <Typography sx={{ width: 42, fontSize: "0.95rem" }}>
                            {star} ★
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={percent}
                            sx={{
                              flexGrow: 1,
                              height: 10,
                              borderRadius: 999,
                              backgroundColor: "rgba(255,255,255,0.14)",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: style.color2,
                              },
                            }}
                          />
                          <Typography sx={{ width: 28, textAlign: "right", fontSize: "0.95rem" }}>
                            {count}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Stack>

                <Divider sx={{ backgroundColor: style.color2, mb: 2 }} />

                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  sx={{ mb: 1 }}
                >
                  <Button
                    size="small"
                    onClick={() => setLeaveReviewModalOpen(true)}
                    sx={{
                      minWidth: 150,
                      color: style.color4,
                      boxShadow: 5,
                      backgroundColor: style.color1,
                      filter: "brightness(1.2)",
                      fontSize: "12pt",
                      borderColor: style.color2,
                      borderWidth: "1px",
                      borderStyle: "solid",
                      "&:hover": { backgroundColor: style.color2 },
                    }}
                  >
                    Leave Review
                  </Button>

                  <Select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    size="small"
                    sx={{
                      minWidth: 160,
                      color: style.color4,
                      boxShadow: 10,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: style.color2,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: style.color3,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: style.color3,
                      },
                      "& .MuiSelect-icon": {
                        color: style.color4,
                      },
                    }}
                  >
                    <MenuItem value="all">All Ratings</MenuItem>
                    {[5, 4, 3, 2, 1].map((num) => (
                      <MenuItem key={num} value={num}>
                        {num} Stars
                      </MenuItem>
                    ))}
                  </Select>

                  <Select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    size="small"
                    sx={{
                      minWidth: 160,
                      color: style.color4,
                      boxShadow: 10,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: style.color2,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: style.color3,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: style.color3,
                      },
                      "& .MuiSelect-icon": {
                        color: style.color4,
                      },
                    }}
                  >
                    <MenuItem value="recent">Most Recent</MenuItem>
                    <MenuItem value="helpful">Most Helpful</MenuItem>
                  </Select>
                </Stack>
              </Box>
            </Box>
          </Paper>

          <Box mt={4}>
            {paginatedReviews.length === 0 ? (
              <Paper
                sx={{
                  p: 3,
                  backgroundColor: style.color1,
                  color: style.color4,
                  borderRadius: 2,
                  boxShadow: 6,
                }}
              >
                <Typography variant="body1">No reviews yet.</Typography>
              </Paper>
            ) : (
              paginatedReviews.map((review) => (
                <Card
                  key={review._id}
                  sx={{
                    mb: 2,
                    backgroundColor: style.color4,
                    color: "black",
                    borderRadius: 2,
                    boxShadow: 6,
                  }}
                >
                  <CardContent>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      spacing={1}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: "1.15rem", fontWeight: 700 }}>
                          {review.userName}
                        </Typography>
                        <Rating value={review.starRating} readOnly precision={0.5} />
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{ color: style.color1, fontStyle: "italic" }}
                      >
                        {formatDate(review.createdAt)}
                      </Typography>
                    </Stack>

                    <Typography
                      variant="body1"
                      sx={{
                        mt: 1.25,
                        mb: 2,
                        fontSize: "1rem",
                        color: "black",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {review.comment}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                      <Tooltip title="Helpful">
                        <IconButton
                          size="small"
                          onClick={() => handleVote(review._id, "helpful")}
                        >
                          <ThumbUpIcon
                            sx={{
                              color:
                                user && review.foundHelpful?.includes(user.id)
                                  ? "rgb(66, 135, 245)"
                                  : style.color2,
                              width: 28,
                              height: 28,
                            }}
                          />
                          <Typography variant="caption" sx={{ fontSize: "0.8em", ml: 0.5 }}>
                            <strong>{review.helpfulCount}</strong>
                          </Typography>
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Not Helpful">
                        <IconButton
                          size="small"
                          onClick={() => handleVote(review._id, "not_helpful")}
                        >
                          <ThumbDownIcon
                            sx={{
                              color:
                                user && review.foundNotHelpful?.includes(user.id)
                                  ? "rgb(66, 135, 245)"
                                  : style.color2,
                              width: 28,
                              height: 28,
                            }}
                          />
                          <Typography variant="caption" sx={{ fontSize: "0.8em", ml: 0.5 }}>
                            <strong>{review.notHelpfulCount}</strong>
                          </Typography>
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}

            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(e, value) => setCurrentPage(value)}
                  color="primary"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: style.color4,
                    },
                  }}
                />
              </Box>
            )}
          </Box>

          <SubscribeRedirectModal
            isOpen={isSubscribeRedirectModalOpen}
            close={closeModal(setSubscribeRedirectModalOpen)}
          />
          <LoginRegisterModal
            isOpen={isLoginRegisterModalOpen}
            close={closeModal(setLoginRegisterModalOpen)}
            loginComplete={loginCallback}
          />
          <LeaveReviewModal
            sku={productSKU}
            isOpen={isLeaveReviewModalOpen}
            close={closeModal(setLeaveReviewModalOpen)}
          />
        </Box>
      )}
    </>
  );
}


// import React, { useState, useContext, useEffect } from "react";
// import axios from "axios";
// import {
//   Box,
//   Card,
//   CardContent,
//   Typography,
//   Rating,
//   Button,
//   Select,
//   MenuItem,
//   IconButton,
//   Tooltip,
//   LinearProgress,
//   useMediaQuery,
//   Pagination,
// } from "@mui/material";
// import { useTheme } from "@mui/material/styles";
// import ThumbUpIcon from "@mui/icons-material/ThumbUp";
// import ThumbDownIcon from "@mui/icons-material/ThumbDown";
// import { UserContext } from "../../context/userContext/userContext";
// import { useStyle } from "../../context/styleContext/styleContext";
// import SubscribeRedirectModal from "../../components/SubscribeRedirectModal/SubscribeRedirectModal";
// import LoginRegisterModal from "./LoginRegisterModal";
// import LeaveReviewModal from "./leaveReviewModal";
// import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";

// const REVIEWS_PER_PAGE = 3;

// export default function ProductReviewPage() {
//   const { user } = useContext(UserContext);
//   const { style } = useStyle();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

//   const [reviews, setReviews] = useState([]);
//   const [productSKU, setProductSKU] = useState(null);
//   const [productData, setProductData] = useState(null);
//   const [filter, setFilter] = useState("all");
//   const [sort, setSort] = useState("recent");
//   const [currentPage, setCurrentPage] = useState(1);
//   const [ratingsBreakdown, setRatingsBreakdown] = useState({});
//   const [averageRating, setAverageRating] = useState(0);

//   const [isSubscribeRedirectModalOpen, setSubscribeRedirectModalOpen] = useState(false);
//   const [isLoginRegisterModalOpen, setLoginRegisterModalOpen] = useState(false);
//   const [isLeaveReviewModalOpen, setLeaveReviewModalOpen] = useState(false);

//   const [isLoginComplete, setIsLoginComplete] = useState(false); 
//   const openModal = setModalFn => () => setModalFn(true);
//   const closeModal = setModalFn => () => setModalFn(false);

//   const [storedReviewId, setStoredReviewId] = useState()
//   const [storedVote, setStoredVote] = useState()

//   const loginCallback = async () => {

//     setIsLoginComplete(true);

//     const token = localStorage.getItem('token')
//     if(!token)
//     {
//       console.log("NO TOKEN")
//       return
//     }

//     const res = await axios.post("/api/mark_review", {
//       token,
//       reviewId:storedReviewId,
//       type:storedVote,
//     });

//     getReviews();
//     setLoginRegisterModalOpen(false);
//     console.log("FINISHED!!")
//   };

//   const openLeaveReviewModal = () => setLeaveReviewModalOpen(true);
//   const [height, setHeight] = useState(window.innerHeight)
  
//   useEffect(() => {
//     document.title = "Parish Audio | Reviews";
//     getReviews();
//   }, [filter, sort]);

//   const getReviews = async () => {
//     const productURI = window.location.pathname.split("/").pop();
//     const { data: productInfo } = await axios.post("/get_product_by_uri", { productURI });

//     if (!productInfo) return;

//     setProductSKU(productInfo.SKU);
//     setProductData(productInfo);

//     const res = await axios.post("/get_all_product_reviews", {
//       productSKU: productInfo.SKU,
//       filter,
//       sort,
//     });

//     const reviews = res.data || [];
//     setReviews(reviews);

//     const starCounts = [0, 0, 0, 0, 0]; // index 0 = 5 stars, index 4 = 1 star
//     let total = 0;
//     let sum = 0;

//     reviews.forEach((r) => {
//       const index = 5 - r.starRating;
//       starCounts[index] = (starCounts[index] || 0) + 1;
//       total++;
//       sum += r.starRating;
//     });

//     const average = total ? (sum / total).toFixed(1) : 0;
//     setAverageRating(average);
//     setRatingsBreakdown(starCounts);
//   };

//   const handleVote = async (reviewId, type) => {

//     const token = localStorage.getItem('token');
//     if (!token ) {
//       setStoredVote(type);
//       setStoredReviewId(reviewId);
//       setLoginRegisterModalOpen(true);
//       return;
//     }
//     if (!user ) {
//       setStoredVote(type);
//       setStoredReviewId(reviewId);
//       setLoginRegisterModalOpen(true);
//       return;
//     }

//     const res = await axios.post("/api/mark_review", {
//       token,
//       reviewId,
//       type,
//     });

//     getReviews();
//     // getReviews();
//   };


//   const formatDate = (dateStr) => {
//     const options = { year: "numeric", month: "short", day: "numeric" };
//     return new Date(dateStr).toLocaleDateString(undefined, options);
//   };

//   const filteredReviews = filter === "all"
//     ? reviews
//     : reviews.filter(r => r.starRating === filter);

//   const totalPages = Math.ceil(filteredReviews.length / REVIEWS_PER_PAGE);
//   const paginatedReviews = filteredReviews.slice(
//     (currentPage - 1) * REVIEWS_PER_PAGE,
//     currentPage * REVIEWS_PER_PAGE
//   );

//   return (
//     <>
//       {!productSKU ? (
//         <Box sx={{ minHeight: "80vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
//           <LoadingSpinner />
//         </Box>
//       ) : (
//         <Box sx={{ maxWidth: 1000, margin: "auto", padding: 2, minHeight:height - 170, }}>
//           {/* Header */}
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: isMobile ? "column" : "row",
//               gap: 2,
//               backgroundColor:style.color1,
//               borderRadius:'8px',
//               alignItems: isMobile ? "center" : "flex-start",
              
//             }}
//           >
//             {/* Product Thumbnail */}
//             <Box
//               sx={{
//                 width: isMobile ? "auto" : "40%",
//                 height: isMobile ? "auto" : "100%",
//                 borderRadius: 2,
//                 //border: `1px solid ${style.color2}`,
//                 overflow: "hidden",
//                 textAlign:'center',
//                 padding:'10px',
                
                
//               }}
//             >
//               <a href={'/products/'+ productData.productURI}>
//               <img
//                 src={productData.images[0].URI}
//                 alt={productData.images[0].altText}
//                 style={{
                  
//                   height: isMobile ? "auto" : "auto",
//                   objectFit: "cover",
//                   borderRadius: 8,
//                   maxHeight:'230px',
//                   maxWidth:'90%',
//                   width:'auto',
//                   margin:'auto',
//                 }}
//               />
//               </a>
//               <h2 style={{marginTop:'10px', fontSize:'1.2em', color:style.color3}}>{productData.name}</h2>
//             </Box>

//             {/* Average Rating & Filters */}
//             <Box
//               sx={{
//                 flexGrow: 1,
//                 backgroundColor:style.color1,
//                 borderRadius: 2,
//                 padding: 1,
//                 width: isMobile ? "95%" : "95%",
//                 color:style.color4,
//               }}
//             >
//               <Box sx={{ display:'flex', flexDirection:'column'}}>
//               </Box>
//               <h1 style={{marginTop:'10px'}}>Reviews</h1>
//               <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 2 }}>
//                 <Rating value={parseFloat(averageRating)} precision={0.1} readOnly />
//                 <Typography variant="h6">{averageRating} / 5</Typography>
//               </Box>
//               {/* Breakdown Chart */}
//               {ratingsBreakdown &&
//                 [5, 4, 3, 2, 1].map((star, i) => {
//                   const count = ratingsBreakdown[5 - star] || 0;
//                   const percent = reviews.length ? (count / reviews.length) * 100 : 0;
//                   return (
//                     <Box key={star} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                       <Typography sx={{ width: 40 }}>{star} ★</Typography>
//                       <LinearProgress
//                         variant="determinate"
//                         value={percent}
//                         sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
//                       />
//                       <Typography sx={{ width: 30 }}>{count}</Typography>
//                     </Box>
//                   );
//                 })}

//               {/* Filter / Sort */}
//               <Box sx={{ display: "flex", mt: 2, pb:1, flexDirection: isMobile ? "column" : "row", }}>
//               <Button size='small' onClick={openLeaveReviewModal} sx={{minWidth:'150px',ml:1, mb:2, color: style.color4, display: 'block', boxShadow:5, backgroundColor: style.color1, filter: 'brightness(1.2)', fontSize: '12pt',  marginTop: '0px', borderColor: style.color2, borderWidth: '1px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color2 } }}>Leave Review</Button>
//               <Select
//                 value={filter}
//                 onChange={(e) => setFilter(e.target.value)}
//                 size="small"
                
//                 sx={{
//                   color: style.color4,
//                   boxShadow:10,
//                   ml:1,
//                   mb:2,
//                   '& .MuiOutlinedInput-notchedOutline': {
//                     borderColor: style.color2,
//                   },
//                   '&:hover .MuiOutlinedInput-notchedOutline': {
//                     borderColor: style.color3,
//                   },
//                   '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
//                     borderColor: style.color3,
//                   },
//                   '& .MuiSelect-icon': {
//                     color: style.color4, // This changes the dropdown arrow
//                   },
//                 }}
//               >
//                 <MenuItem value="all">All Ratings</MenuItem>
//                 {[5, 4, 3, 2, 1].map(num => (
//                   <MenuItem key={num} value={num}>{num} Stars</MenuItem>
//                 ))}
//               </Select>
//                 <Select sx={{
//                   color: style.color4,
//                   ml:1,
//                   boxShadow:10,
//                   size:'small',
//                   mb:2,
//                   '& .MuiOutlinedInput-notchedOutline': {
//                     borderColor: style.color2,
//                   },
//                   '&:hover .MuiOutlinedInput-notchedOutline': {
//                     borderColor: style.color3,
//                   },
//                   '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
//                     borderColor: style.color3,
//                   },
//                   '& .MuiSelect-icon': {
//                     color: style.color4, // This changes the dropdown arrow
//                   },
//                 }}
//                  value={sort} onChange={(e) => setSort(e.target.value)} size="small">
//                   <MenuItem value="recent">Most Recent</MenuItem>
//                   <MenuItem value="helpful">Most Helpful</MenuItem>
//                 </Select>
//                 </Box>
//             </Box>
//           </Box>

//           {/* Reviews */}
//           <Box mt={4}>
//             {paginatedReviews.length === 0 ? (
//               <Typography variant="body1">No reviews yet.</Typography>
//             ) : (
//               paginatedReviews.map(review => (
//                 <Card key={review._id} sx={{ mb: 2, backgroundColor:style.color4, color:'black' }}>
//                   <CardContent>
//                     <Box sx={{ display: "flex", flex:1, justifyContent: "left", alignItems: "center"}}>
//                       <Typography sx={{fontSize:'1.3em'}}>{review.userName}</Typography>
//                       <Rating value={review.starRating} readOnly precision={0.5} sx={{ml:2}}/>
//                     </Box>

//                     <Typography variant="caption" sx={{ color: style.color1, fontStyle: "italic", display: "block" }}>
//                       {formatDate(review.createdAt)}
//                     </Typography>

//                     <Typography variant="body2" sx={{ mt: 0.5, mb:2, fontSize:'1em',backgroundColor:style.color4, color:'black' }}>{review.comment}</Typography>

//                     <Box sx={{ display: "flex", gap: 1, mt: 1, backgroundColor:style.color4, }}>
//                     <Tooltip title="Helpful">
//                     <IconButton
//                       size="small"
//                       onClick={() => handleVote(review._id, "helpful")}
//                     >
//                       {user && (
//                           <ThumbUpIcon
//                           sx={{
//                             color: review.foundHelpful.includes(user.id)
//                               ? 'rgb(66, 135, 245)' // highlighted color
//                               : style.color2, // default color
//                             width:'30px',
//                             height:'30px'
//                           }}
//                           />
//                       )}
//                        {!user && (
//                       <ThumbUpIcon
//                         sx={{
//                           color: style.color2,
//                           width:'30px',
//                           height:'30px'
//                         }}
//                       />)}
//                       <Typography variant="caption" sx={{fontSize:'0.8em', ml: 0.5 }}>
//                         <strong>{review.helpfulCount}</strong>
//                       </Typography>
//                     </IconButton>
//                   </Tooltip>
//                       <Tooltip title="Not Helpful">
//                         <IconButton
//                       size="small"
//                       onClick={() => handleVote(review._id, "not_helpful")}
//                     >
//                       {user && (
//                         <ThumbDownIcon
//                         sx={{
//                           color: review.foundNotHelpful.includes(user.id)
//                             ? 'rgb(66, 135, 245)' // highlighted color
//                             : style.color2, // default color
//                           width:'30px',
//                           height:'30px'
//                         }}
//                       />

//                       )}
//                       {!user &&(
//                         <ThumbDownIcon
//                         sx={{
//                           color: style.color2,
//                           width:'30px',
//                           height:'30px'
//                         }}
//                         />
//                       )}
                     
//                       <Typography variant="caption" sx={{fontSize:'0.8em', ml: 0.5 }}>
//                         <strong>{review.notHelpfulCount}</strong>
//                       </Typography>
//                     </IconButton>
//                       </Tooltip>
//                     </Box>
//                   </CardContent>
//                 </Card>
//               ))
//             )}

//             {/* Pagination */}
//             {totalPages > 1 && (
//               <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
//                 <Pagination
//                   count={totalPages}
//                   page={currentPage}
//                   onChange={(e, value) => setCurrentPage(value)}
//                   color='primary'
//                   sx={{color:style.color4}}
//                 />
//               </Box>
//             )}
//           </Box>

//           {/* Modals */}
//           <SubscribeRedirectModal isOpen={isSubscribeRedirectModalOpen} close={closeModal(setSubscribeRedirectModalOpen)} />
//           <LoginRegisterModal isOpen={isLoginRegisterModalOpen} close={closeModal(setLoginRegisterModalOpen)} loginComplete={loginCallback}/>
//           <LeaveReviewModal sku={productSKU} isOpen={isLeaveReviewModalOpen} close={closeModal(setLeaveReviewModalOpen)} />

//         </Box>
//       )}
//     </>
//   );
// }


